import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  president_letter: "",
  president_name: "",
  price: "$2",
  cover_subtitle: "Official Race Day Program",
  sponsors_note: "Please support the businesses that support your club.",
  closing_note: "Thank you for supporting Mid Western Speedway Darlington — Mortlake Car Club Inc.",
  release_date: "",
};

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">{label}</label>
      {children}
      {hint && <p className="font-mono text-[10px] text-clay-muted/70 mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminProgram({ token }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const loadProgram = useCallback(
    async (id) => {
      try {
        const { data } = await api.get(`/admin/program/${id}`, { headers });
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(data.program || {}).filter(([k]) => k in EMPTY)) });
      } catch {
        toast.error("Failed to load program");
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (eventId) loadProgram(eventId);
  }, [eventId, loadProgram]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!eventId) {
      toast.error("Choose a meeting first");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/program/${eventId}`, form, { headers });
      toast.success("Program saved — open the printable version to check it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-10">
      <section className="border border-clay-border bg-clay-card p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="flex-1 min-w-[260px]">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">Meeting</label>
            <select data-testid="program-event-select" value={eventId} onChange={(e) => setEventId(e.target.value)} className={inputCls}>
              <option value="">Choose the meeting this program is for…</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })} — {e.title}
                </option>
              ))}
            </select>
          </div>
          {eventId && (
            <button
              data-testid="program-open-button"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/program/${eventId}/preview`, {}, { headers });
                  window.open(`/program/${eventId}?key=${data.key}`, "_blank");
                } catch {
                  toast.error("Couldn't open preview");
                }
              }}
              className="flex items-center gap-2 border border-clay-orange text-clay-orange hover:bg-clay-orange hover:text-clay-bg font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors"
            >
              <ExternalLink className="w-4 h-4" /> View &amp; Print
            </button>
          )}
        </div>

        {eventId && (
          <div className="space-y-10">
            <div>
              <h3 className="font-display font-bold uppercase tracking-tight text-xl mb-4 text-clay-orange">Page 1 — Cover</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Price tag" hint='Shown in the red circle — e.g. "$2" or "Gold Coin"'>
                  <input data-testid="program-price-input" value={form.price} onChange={set("price")} className={inputCls} />
                </Field>
                <Field label="Cover subtitle" hint='Above the meeting title — e.g. "Official Race Day Program"'>
                  <input data-testid="program-subtitle-input" value={form.cover_subtitle} onChange={set("cover_subtitle")} className={inputCls} />
                </Field>
              </div>
            </div>

            <div>
              <h3 className="font-display font-bold uppercase tracking-tight text-xl mb-4 text-clay-orange">Page 2 — President's Letter</h3>
              <div className="space-y-4">
                <Field label="Letter" hint="A welcome message from the President, printed on page two">
                  <textarea data-testid="program-letter-input" rows={8} value={form.president_letter} onChange={set("president_letter")} className={`${inputCls} resize-y`} placeholder="Welcome to Mid Western Speedway Darlington…" />
                </Field>
                <Field label="Signed by" hint="Name printed under the letter">
                  <input data-testid="program-president-input" value={form.president_name} onChange={set("president_name")} className={inputCls} placeholder="e.g. Ian Ross" />
                </Field>
              </div>
            </div>

            <div>
              <h3 className="font-display font-bold uppercase tracking-tight text-xl mb-1 text-clay-orange">Page 3 — Nominations</h3>
              <p className="font-mono text-[10px] text-clay-muted/70 mb-4">
                Built automatically from the meeting's nominations (Admin → Nominations tab) — one page per class with entry counts, car numbers and driver names. Nothing to edit here.
              </p>
            </div>

            <div>
              <h3 className="font-display font-bold uppercase tracking-tight text-xl mb-4 text-clay-orange">Back Cover — Sponsors</h3>
              <p className="font-mono text-[10px] text-clay-muted/70 mb-4">
                Sponsor logos come straight from your Sponsors tab — update them there. The wording below is editable.
              </p>
              <div className="space-y-4">
                <Field label="Sponsors intro line">
                  <input data-testid="program-sponsors-note-input" value={form.sponsors_note} onChange={set("sponsors_note")} className={inputCls} />
                </Field>
                <Field label="Closing thank-you line">
                  <input data-testid="program-closing-input" value={form.closing_note} onChange={set("closing_note")} className={inputCls} />
                </Field>
              </div>
            </div>

            <div>
              <h3 className="font-display font-bold uppercase tracking-tight text-xl mb-4 text-clay-orange">Availability</h3>
              <Field
                label="On sale from (optional)"
                hint="The Program button appears on the public calendar once this program is saved and this date has passed — leave blank to sell straight away"
              >
                <input data-testid="program-release-input" type="date" value={form.release_date} onChange={set("release_date")} className={`${inputCls} max-w-xs`} />
              </Field>
            </div>

            <button
              data-testid="program-save-button"
              onClick={save}
              disabled={saving}
              className="bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-10 py-4 transition-colors duration-300 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Program
            </button>
          </div>
        )}
      </section>

      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted">
        Each meeting keeps its own program — saved content reloads when you pick it again · use View &amp; Print to check the finished booklet
      </p>
    </div>
  );
}
